import React, { createContext, useContext, useState, useCallback } from "react";
import {
  filterSettings as defaultFilterSettings,
  FilterDisplaySettings,
} from "../utils/filterSettings";
import {
  fetchFilterSettings,
  saveFilterSettings,
} from "../utils/filterSettingsApi";
import { useAuth } from "./AuthContext";

type TabType = "overview" | "frame" | "quality" | "guiding";

interface UIContextType {
  selectedTab: TabType;
  frameTypeFilter: string;
  filterTypeFilter: string;
  setSelectedTab: (tab: TabType) => void;
  setFrameTypeFilter: (filter: string) => void;
  setFilterTypeFilter: (filter: string) => void;
  filterSettings: Record<string, FilterDisplaySettings> | null;
  filterSettingsLoading: boolean;
  setFilterSettings: (
    valueOrFn: React.SetStateAction<Record<string, FilterDisplaySettings>>
  ) => Promise<void>;
}

const UIContext = createContext<UIContextType | undefined>(undefined);

export const UIProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedTab, setSelectedTab] = useState<TabType>("overview");
  const [frameTypeFilter, setFrameTypeFilter] = useState<string>("All");
  const [filterTypeFilter, setFilterTypeFilter] = useState<string>("All");
  const [filterSettings, _setFilterSettings] = useState<Record<
    string,
    FilterDisplaySettings
  > | null>(null);
  const [filterSettingsLoading, setFilterSettingsLoading] = useState(false);
  // const [filterSettingsError, setFilterSettingsError] = useState<string | null>(
  //   null
  // ); // Commented out as it's currently unused, will be needed for error handling

  const { isAuthenticated, token } = useAuth();

  // Fetch from DB only when authenticated
  React.useEffect(() => {
    let mounted = true;

    if (isAuthenticated && token) {
      console.log("User is authenticated, fetching filter settings");
      setFilterSettingsLoading(true);

      fetchFilterSettings()
        .then((data) => {
          if (mounted) {
            _setFilterSettings(data);
            // setFilterSettingsError(null);
          }
        })
        .catch(() => {
          if (mounted) {
            _setFilterSettings(defaultFilterSettings);
            // setFilterSettingsError(
            //   "Could not load filter settings from database. Using defaults."
            // );
          }
        })
        .finally(() => {
          if (mounted) setFilterSettingsLoading(false);
        });
    } else {
      console.log("User is not authenticated, using default filter settings");
      _setFilterSettings(defaultFilterSettings);
      setFilterSettingsLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, [isAuthenticated, token]);

  const setFilterSettings = useCallback(
    async (
      valueOrFn: React.SetStateAction<Record<string, FilterDisplaySettings>>
    ) => {
      const currentSettings = filterSettings || defaultFilterSettings;
      const newSettings =
        typeof valueOrFn === "function"
          ? valueOrFn(currentSettings)
          : valueOrFn;

      try {
        if (isAuthenticated && token) {
          // Save to DB only if authenticated
          await saveFilterSettings(newSettings);
        }
        // Always update local state
        _setFilterSettings(newSettings);
        // setFilterSettingsError(null);
      } catch (error) {
        console.error("Failed to save filter settings:", error);
        // setFilterSettingsError("Failed to save filter settings to database.");
        throw error;
      }
    },
    [filterSettings, isAuthenticated, token]
  );

  const value = {
    selectedTab,
    frameTypeFilter,
    filterTypeFilter,
    setSelectedTab,
    setFrameTypeFilter,
    setFilterTypeFilter,
    filterSettings,
    filterSettingsLoading,
    setFilterSettings,
  };

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error("useUI must be used within a UIProvider");
  }
  return context;
};
